import * as React from "react";
// Components
import { useTheme } from "@fluentui/react";
import { Dropdown as FluentDropdown, mergeClasses, Option, OptionGroup, Tooltip } from "@fluentui/react-components";
// Constants
import { PlannerTheme } from "@ms/uno-constants/lib/local/ThemeConstants";
// Resources
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
// Styles
import { useStyles } from "./Dropdown.styles";
import { useDebouncedCallback } from "../hooks/useDebouncedCallback";
import { customizeStyles } from "../utilities/StyleUtilities";
/**
 * Presentational Dropdown component using Fluent UI v9.
 */ export const Dropdown = ({ options, onOptionChange, selectedKey, onClear, label, debounceInterval, customStyles, placeholder, onRenderOption, onRenderTitle, disabled, dropdownRef, title, ariaLabel, disableTooltip, ...props })=>{
    const classNames = customizeStyles(useStyles(), customStyles);
    const clearable = onClear != null;
    const selectedOption = options.find((o)=>o.key === selectedKey);
    const isHighContrast = useTheme().name === PlannerTheme.HighContrast;
    const isOptionSelected = (key)=>Array.isArray(selectedKey) ? selectedKey.includes(key) : selectedKey === key;
    const renderOption = (option)=>{
        if (option.isHeading) {
            return /*#__PURE__*/ React.createElement("div", {
                role: "presentation",
                key: String(option.key),
                className: classNames.headingContainer
            }, option.text);
        }
        return /*#__PURE__*/ React.createElement(Option, {
            "data-testid": "dropdown-option",
            key: String(option.key),
            value: String(option.key),
            text: option.text,
            disabled: option.disabled,
            "data-index": option.index,
            className: mergeClasses(classNames.dropdownItem, isOptionSelected(option.key) && classNames.selectedDropdownItem, isHighContrast && classNames.highContrastDropdownIcon)
        }, onRenderOption ? onRenderOption(option) : /*#__PURE__*/ React.createElement("div", {
            className: classNames.optionButton
        }, option?.icon && /*#__PURE__*/ React.createElement("span", {
            className: classNames.optionIcon
        }, option.icon), /*#__PURE__*/ React.createElement("span", {
            className: classNames.optionText
        }, option.text)));
    };
    const footerActionOptions = options.filter((option)=>option.isFooterAction);
    // Fluent draws a group's divider as a border *below* it and skips the last group, so splitting
    // the footer actions into their own trailing group is what puts a rule above them.
    const optionElements = footerActionOptions.length ? /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(OptionGroup, null, options.filter((option)=>!option.isFooterAction).map(renderOption)), /*#__PURE__*/ React.createElement(OptionGroup, null, footerActionOptions.map(renderOption))) : options.map(renderOption);
    const debouncedOnChange = useDebouncedCallback(onOptionChange, debounceInterval ?? 0);
    const handleOptionSelect = (optionValue)=>{
        //Fluent Dropdown optionValue is always string, so we need to map it back to the original type T
        const selectedOption = options.find((option)=>String(option.key) === optionValue);
        if (selectedOption) {
            debounceInterval ? debouncedOnChange(selectedOption.key) : onOptionChange?.(selectedOption.key);
        }
    };
    const tooltipContent = (title || selectedOption?.text || placeholder || "").trim();
    const dropdownElement = /*#__PURE__*/ React.createElement(FluentDropdown, {
        "data-testid": "dropdown",
        ...props,
        // selectedOptions can be undefined as per props but it is crashing in that case, hence passing empty array
        selectedOptions: selectedKey !== undefined ? Array.isArray(selectedKey) ? selectedKey.map(String) : [
            String(selectedKey)
        ] : [],
        clearable: clearable,
        clearButton: {
            "aria-label": Strings.SharedComponents_DropdownClearSelectionAriaLabel
        },
        disabled: disabled,
        value: selectedOption ? String(selectedKey) : "",
        placeholder: placeholder,
        className: classNames.dropdown,
        "aria-label": ariaLabel || label,
        title: disableTooltip ? title : undefined,
        onOptionSelect: (_, data)=>{
            if (data.optionValue !== undefined) {
                handleOptionSelect(data.optionValue);
            } else {
                onClear?.();
            }
        },
        listbox: {
            className: classNames.listbox
        },
        ref: dropdownRef,
        onKeyDown: (e)=>{
            e.stopPropagation();
        },
        button: {
            className: classNames.button,
            ...onRenderTitle ? {
                children: onRenderTitle(selectedOption)
            } : selectedOption ? {
                children: /*#__PURE__*/ React.createElement("div", {
                    className: classNames.dropdownButton
                }, selectedOption.icon && /*#__PURE__*/ React.createElement("span", {
                    className: classNames.optionIcon
                }, selectedOption.icon), /*#__PURE__*/ React.createElement("span", {
                    className: classNames.optionText
                }, selectedOption.text))
            } : {}
        }
    }, optionElements);
    return /*#__PURE__*/ React.createElement("div", {
        className: classNames.root
    }, label && /*#__PURE__*/ React.createElement("label", {
        className: classNames.label
    }, label), disableTooltip ? dropdownElement : tooltipContent ? /*#__PURE__*/ React.createElement(Tooltip, {
        content: tooltipContent,
        relationship: "label",
        positioning: "above",
        withArrow: true
    }, dropdownElement) : dropdownElement);
};
